//Imports
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addUnsyncedEntry,
  updateUnsyncedEntry,
  deleteUnsyncedEntry,
  cacheEntry,
  deleteCachedEntry,
  cacheHikes,
  getCachedHikes,
  getUnsyncedEntries,
  getCachedEntriesByHike,
  replaceCachedEntriesForHike
} from '../utils/db';
import SyncStatus from '../components/SyncStatus';

const EMPTY_LOCATION = { name: '', lat: null, lng: null };

// Today's date (YYYY-MM-DD) in the user's local timezone.
// toISOString() uses UTC, which rolls over to tomorrow in the evening in the Americas.
const todayLocal = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

// Normalise an entry date ("2025-01-02" or "2025-01-02T00:00:00.000Z") to YYYY-MM-DD
const toDateKey = (date) => (typeof date === 'string' ? date : new Date(date).toISOString()).slice(0, 10);

const hasLocation = (location) => !!location && (!!location.name || location.lat != null);

const emptyForm = (date, locationFrom = EMPTY_LOCATION) => ({
  date,
  kmTravelled: '',
  rpe: '5',
  expenses: [{ category: 'General', amount: '' }],
  mood: '5',
  sleepQuality: '5',
  overallFeeling: '5',
  caloriesSpent: '',
  weatherTemp: 'Mild',
  weatherType: 'Sunny',
  notes: '',
  locationFrom,
  locationTo: EMPTY_LOCATION
});

const entryToForm = (entry) => ({
  date: toDateKey(entry.date),
  kmTravelled: entry.kmTravelled?.toString() || '',
  rpe: entry.rpe?.toString() || '5',
  expenses: entry.expenses && entry.expenses.length > 0
    ? entry.expenses.map(exp => ({ category: exp.category, amount: exp.amount.toString() }))
    : [{ category: 'General', amount: '' }],
  mood: entry.mood?.toString() || '5',
  sleepQuality: entry.sleepQuality?.toString() || '5',
  overallFeeling: entry.overallFeeling?.toString() || '5',
  caloriesSpent: entry.caloriesSpent?.toString() || '',
  weatherTemp: entry.weatherTemp || 'Mild',
  weatherType: entry.weatherType || 'Sunny',
  notes: entry.notes || '',
  locationFrom: entry.locationFrom || EMPTY_LOCATION,
  locationTo: entry.locationTo || EMPTY_LOCATION
});

// All entries for a hike, one per date. Server entries are cached so they are
// still available after days offline; unsynced (offline) entries win over them.
const getEntriesForHike = async (hikeId) => {
  let serverEntries = null;

  if (navigator.onLine) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/hikes/${hikeId}/entries`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        serverEntries = await response.json();
        await replaceCachedEntriesForHike(hikeId, serverEntries);
      }
    } catch (err) {
      console.log('Could not fetch server entries, using cached only');
    }
  }

  if (!serverEntries) {
    serverEntries = await getCachedEntriesByHike(hikeId);
  }

  const unsyncedEntries = (await getUnsyncedEntries())
    .filter(e => e.hikeId === hikeId)
    .sort((a, b) => a.tempId - b.tempId);

  const byDate = new Map();
  serverEntries.forEach(e => byDate.set(toDateKey(e.date), e));
  unsyncedEntries.forEach(e => byDate.set(toDateKey(e.date), { ...e, isUnsynced: true }));
  return [...byDate.values()];
};

function UpdateHike() {
  const navigate = useNavigate();
  const [hikes, setHikes] = useState([]);
  const [selectedHikeId, setSelectedHikeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [existingEntryId, setExistingEntryId] = useState(null); // server _id
  const [existingTempId, setExistingTempId] = useState(null); // unsynced (offline) entry tempId

  const [formData, setFormData] = useState(() => emptyForm(todayLocal()));

  const [gpsStatus, setGpsStatus] = useState('idle'); // idle, loading, success, error
  const [gpsError, setGpsError] = useState('');

  // Incremented whenever the hike/date being edited changes, so slow lookups
  // and GPS fixes started for a different date are ignored.
  const formVersionRef = useRef(0);

  const weatherTempOptions = [
    { value: 'Hot', label: 'Hot 🔥' },
    { value: 'Mild', label: 'Mild 🌤️' },
    { value: 'Cold', label: 'Cold 🥶' }
  ];

  const weatherTypeOptions = [
    { value: 'Sunny', label: 'Sunny ☀️' },
    { value: 'Cloudy', label: 'Cloudy ☁️' },
    { value: 'Rain', label: 'Rain 🌧️' },
    { value: 'Storm', label: 'Storm ⛈️' },
    { value: 'Windy', label: 'Windy 🌬️' },
    { value: 'Snow', label: 'Snow 🌨️' }
  ];

  // Capture GPS location
const captureGPS = useCallback(() => {
  if (!navigator.geolocation) {
    setGpsStatus('error');
    setGpsError('GPS not supported');
    return;
  }

  const version = formVersionRef.current;
  setGpsStatus('loading');
  
  navigator.geolocation.getCurrentPosition(
    (position) => {
      // The user moved to another date while waiting for a fix - don't write
      // today's position into that entry
      if (version !== formVersionRef.current) {
        setGpsStatus('idle');
        return;
      }
      setFormData(prev => ({
        ...prev,
        locationTo: {
          ...prev.locationTo,
          lat: position.coords.latitude,
          lng: position.coords.longitude
        }
      }));
      setGpsStatus('success');
      console.log('📍 GPS captured:', position.coords.latitude, position.coords.longitude);
    },
    (error) => {
      if (version !== formVersionRef.current) {
        setGpsStatus('idle');
        return;
      }
      setGpsStatus('error');
      setGpsError(error.message);
      console.error('📍 GPS error:', error.message);
    },
    { 
      enableHighAccuracy: true, 
      timeout: 10000, 
      maximumAge: 60000 
    }
  );
}, []);

// Load the entry for this hike/date, or start a new one whose "From" is the
// previous day's "To"
const checkExistingEntry = useCallback(async (date, hikeId) => {
  if (!hikeId || !date) return;

  const version = ++formVersionRef.current;
  setGpsStatus('idle');

  let entries = [];
  try {
    entries = await getEntriesForHike(hikeId);
  } catch (err) {
    console.error('Error checking existing entry:', err);
  }

  // A newer hike/date was selected while this lookup was running
  if (version !== formVersionRef.current) return;

  const existing = entries.find(e => toDateKey(e.date) === date);

  if (existing) {
    setFormData(entryToForm(existing));
    setExistingEntryId(existing.isUnsynced ? null : existing._id);
    setExistingTempId(existing.isUnsynced ? existing.tempId : null);
    return;
  }

  const previous = entries
    .filter(e => toDateKey(e.date) < date && hasLocation(e.locationTo))
    .sort((a, b) => toDateKey(b.date).localeCompare(toDateKey(a.date)))[0];

  setFormData(emptyForm(date, previous ? { ...EMPTY_LOCATION, ...previous.locationTo } : EMPTY_LOCATION));
  setExistingEntryId(null);
  setExistingTempId(null);
}, []);

  useEffect(() => {
    fetchHikes();

    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

// Check URL parameters for date and hikeId
useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const dateParam = params.get('date');
  const hikeIdParam = params.get('hikeId');
  
  if (dateParam) {
    setFormData(prev => ({ ...prev, date: dateParam }));
  }
  
  if (hikeIdParam) {
    setSelectedHikeId(hikeIdParam);
  }
}, []);


// Load the entry whenever the hike or date changes
useEffect(() => {
  // Skip if no hike selected or no date
  if (!selectedHikeId || !formData.date) return;
  
  // Debounce to prevent multiple rapid calls
  const timeoutId = setTimeout(() => {
    console.log('⏰ Debounced check for:', formData.date);
    checkExistingEntry(formData.date, selectedHikeId);
  }, 300);
  
  return () => clearTimeout(timeoutId);
}, [selectedHikeId, formData.date, checkExistingEntry]);

const handleExpenseChange = (index, field, value) => {
  const newExpenses = [...formData.expenses];
  newExpenses[index][field] = value;
  setFormData(prev => ({
    ...prev,
    expenses: newExpenses
  }));
  setError('');
  setSuccess('');
};

const addExpenseRow = () => {
  setFormData(prev => ({
    ...prev,
    expenses: [...prev.expenses, { category: 'General', amount: '' }]
  }));
};

const removeExpenseRow = (index) => {
  if (formData.expenses.length > 1) {
    const newExpenses = formData.expenses.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      expenses: newExpenses
    }));
  }
};

const fetchHikes = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hikes`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch hikes');
    }

    const data = await response.json();
    
    // Cache hikes for offline use
    await cacheHikes(data);
    
    setHikes(data);
    
    // Auto-select first hike if none selected (keeps a hikeId from the URL)
    if (data.length > 0) {
      setSelectedHikeId(prev => prev || data[0]._id);
    }
  } catch (err) {
    // Try to load from cache if offline
    const cachedHikes = await getCachedHikes();
    
    if (cachedHikes && cachedHikes.length > 0) {
      setHikes(cachedHikes);
      setSelectedHikeId(prev => prev || cachedHikes[0]._id);
      setError(''); // Clear error if we have cached data
    } else {
      setError('Failed to load hikes. Please try again.');
    }
    console.error('Error fetching hikes:', err);
  }
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validation
    if (!selectedHikeId) {
      setError('Please select a hike');
      setLoading(false);
      return;
    }

    // Validate that at least SOMETHING is being logged
    const hasDistance = formData.kmTravelled && parseFloat(formData.kmTravelled) > 0;
    const hasExpenses = formData.expenses && formData.expenses.some(exp => exp.amount && parseFloat(exp.amount) > 0);
    const hasNotes = formData.notes && formData.notes.trim() !== '';

    if (!hasDistance && !hasExpenses && !hasNotes) {
      setError('Please enter at least distance, expenses, or notes');
      setLoading(false);
      return;
    }

    try {
      // Filter out empty expenses and format them
      const validExpenses = formData.expenses
        .filter(exp => exp.amount && parseFloat(exp.amount) > 0)
        .map(exp => ({
          category: exp.category,
          amount: parseFloat(exp.amount)
        }));

      const entryData = {
        hikeId: selectedHikeId,
        date: formData.date,
        kmTravelled: parseFloat(formData.kmTravelled) || 0,
        rpe: parseInt(formData.rpe),
        expenses: validExpenses,
        mood: parseInt(formData.mood),
        sleepQuality: parseInt(formData.sleepQuality),
        overallFeeling: parseInt(formData.overallFeeling),
        caloriesSpent: parseInt(formData.caloriesSpent) || 0,
        weatherTemp: formData.weatherTemp,
        weatherType: formData.weatherType,
        notes: formData.notes,
        locationFrom: formData.locationFrom,
        locationTo: formData.locationTo
      };

        if (isOnline) {
          let response;
          
          if (existingEntryId) {
            // UPDATE existing entry
            response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/hikes/${selectedHikeId}/entries/${existingEntryId}`,
              {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(entryData)
              }
            );
          } else {
            // CREATE new entry
            response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/hikes/${selectedHikeId}/entries`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(entryData)
              }
            );
          }

          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || 'Failed to save entry');
          }

          const savedEntry = await response.json();
          await cacheEntry(savedEntry);

          // The offline copy of this entry is now on the server
          if (existingTempId) {
            await deleteUnsyncedEntry(existingTempId);
          }

          setSuccess(existingEntryId || existingTempId ? '✅ Entry updated successfully!' : '✅ Entry saved successfully!');
        } else {
        // OFFLINE: Save to IndexedDB for later sync, editing the existing offline copy if there is one
        if (existingTempId) {
          await updateUnsyncedEntry(existingTempId, entryData);
        } else {
          await addUnsyncedEntry(entryData);
        }
        setSuccess('📴 Entry saved offline. Will sync when online.');
      }

      // Reset form to today. Changing the date reloads it via the effect; if we
      // were already on today, reload it here so the saved entry is shown.
      const today = todayLocal();
      const newFrom = hasLocation(formData.locationTo) ? formData.locationTo : formData.locationFrom;

      setFormData(emptyForm(today, newFrom));
      setExistingEntryId(null);
      setExistingTempId(null);
      setGpsStatus('idle');

      if (formData.date === today) {
        checkExistingEntry(today, selectedHikeId);
      }

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      setError(err.message || 'Failed to save entry');
      console.error('Error saving entry:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
  if (!existingEntryId && !existingTempId) {
    alert('No entry to delete');
    return;
  }

  if (!window.confirm('Are you sure you want to delete this entry? This cannot be undone.')) {
    return;
  }

  setLoading(true);
  try {
    // Offline-only entry that was never synced
    if (existingTempId) {
      await deleteUnsyncedEntry(existingTempId);
    }

    if (existingEntryId) {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/hikes/${selectedHikeId}/entries/${existingEntryId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete entry');
      }

      await deleteCachedEntry(existingEntryId);
    }

    alert('Entry deleted successfully!');
    
    // Reset form and navigate to report
    setFormData(emptyForm(todayLocal()));
    setExistingEntryId(null);
    setExistingTempId(null);
    navigate('/hike/report');
  } catch (err) {
    setError(err.message || 'Failed to delete entry');
    console.error('Error deleting entry:', err);
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="max-w-2xl mx-auto">
      <SyncStatus />
      
      <div className="bg-white shadow-md rounded-lg px-8 pt-6 pb-8 mb-40">
        <div className="mb-3">
          <h2 className="text-3xl font-bold text-gray-800">Update Hike</h2>
          <p className="text-gray-600 mt-1">Log your daily trail progress</p>
          
          {!isOnline && (
            <div className="mt-2 bg-yellow-50 border-l-4 border-yellow-400 p-2">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-2xl">📴</span>
                </div>
                <div className="ml-2">
                  <p className="text-xs text-yellow-700">
                    <strong>Offline Mode:</strong> Your entry will be saved locally and synced when you're back online.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}

        {hikes.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">You haven't created any hikes yet.</p>
            <button
              onClick={() => navigate('/hike/setup')}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
            >
              Setup Your First Hike
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Hike Selection */}
            <div className="mb-1">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Select Hike
              </label>
              <select
                value={selectedHikeId}
                onChange={(e) => {
                  setSelectedHikeId(e.target.value);
                }}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                <option value="">Choose a hike...</option>
                {hikes.map(hike => (
                  <option key={hike._id} value={hike._id}>
                    {hike.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Date */}
            <div className="mb-1">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Date
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            {/* Location From */}
            <div className="mb-1">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Starting From
              </label>
              <input
                type="text"
                value={formData.locationFrom.name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  locationFrom: { ...prev.locationFrom, name: e.target.value }
                }))}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="e.g., Bluff, Bob's Hut, km marker 42"
              />
              {formData.locationFrom.lat && (
                <p className="text-xs text-gray-500 mt-1">
                  📍 {formData.locationFrom.lat.toFixed(4)}, {formData.locationFrom.lng.toFixed(4)}
                </p>
              )}
            </div>

            {/* Location To */}
            <div className="mb-1">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Ending At
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.locationTo.name}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    locationTo: { ...prev.locationTo, name: e.target.value }
                  }))}
                  className="shadow border rounded flex-1 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Click the pin to update GPS location"
                />
                <button
                  type="button"
                  onClick={() => captureGPS()}
                  className={`px-3 py-2 rounded font-bold text-white ${
                    gpsStatus === 'loading' ? 'bg-yellow-500' :
                    gpsStatus === 'success' ? 'bg-green-500' :
                    gpsStatus === 'error' ? 'bg-red-500' :
                    'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  {gpsStatus === 'loading' ? '⏳' :
                  gpsStatus === 'success' ? '✅' :
                  gpsStatus === 'error' ? '❌' : '📍'}
                </button>
              </div>
              {gpsStatus === 'success' && formData.locationTo.lat && (
                <p className="text-xs text-green-600 mt-1">
                  📍 GPS: {formData.locationTo.lat.toFixed(4)}, {formData.locationTo.lng.toFixed(4)}
                </p>
              )}
              {gpsStatus === 'error' && (
                <p className="text-xs text-red-600 mt-1">
                  ⚠️ {gpsError || 'Could not get location'}
                </p>
              )}
            </div>
            {/* KM Travelled */}
            <div className="mb-1">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                KM Travelled
              </label>
              <input
                type="number"
                name="kmTravelled"
                value={formData.kmTravelled}
                onChange={handleChange}
                step="0.1"
                min="0"
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="0.0"
              />
            </div>

            {/* RPE */}
            <div className="mb-1">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                RPE (Rate of Perceived Exertion) /10
              </label>
              <select
                name="rpe"
                value={formData.rpe}
                onChange={handleChange}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                {[1,2,3,4,5,6,7,8,9,10].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

           {/* Expenses */}
            <div className="mb-1">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Expenses
              </label>
              
              {formData.expenses.map((expense, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <select
                    value={expense.category}
                    onChange={(e) => handleExpenseChange(index, 'category', e.target.value)}
                    className="shadow border rounded w-1/2 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  >
                    <option value="Food">Food</option>
                    <option value="Accommodation">Accommodation</option>
                    <option value="Travel">Travel</option>
                    <option value="Equipment">Equipment</option>
                    <option value="General">General</option>
                    <option value="Pre-Hike">Pre-Hike</option>
                  </select>
                  
                  <input
                    type="number"
                    value={expense.amount}
                    onChange={(e) => handleExpenseChange(index, 'amount', e.target.value)}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    className="shadow border rounded w-1/3 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  />
                  
                  <button
                    type="button"
                    onClick={() => addExpenseRow()}
                    className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-3 rounded focus:outline-none focus:shadow-outline"
                  >
                    ➕
                  </button>
                  
                  {formData.expenses.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeExpenseRow(index)}
                      className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-3 rounded focus:outline-none focus:shadow-outline"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              ))}
              
              <p className="text-xs text-gray-600 mt-1">
                Add multiple expense categories per day. Use "Pre-Hike" for gear/flights bought before the trail.
              </p>
            </div>

            {/* Mood */}
            <div className="mb-1">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Mood /10
              </label>
              <select
                name="mood"
                value={formData.mood}
                onChange={handleChange}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                {[1,2,3,4,5,6,7,8,9,10].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            {/* Sleep Quality */}
            <div className="mb-1">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Last Night Sleep Quality /10
              </label>
              <select
                name="sleepQuality"
                value={formData.sleepQuality}
                onChange={handleChange}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                {[1,2,3,4,5,6,7,8,9,10].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            {/* Overall Feeling */}
            <div className="mb-1">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Overall Feeling /10
              </label>
              <select
                name="overallFeeling"
                value={formData.overallFeeling}
                onChange={handleChange}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                {[1,2,3,4,5,6,7,8,9,10].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            {/* Calories Spent */}
            <div className="mb-1">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Calories Spent - optional
              </label>
              <input
                type="number"
                name="caloriesSpent"
                value={formData.caloriesSpent}
                onChange={handleChange}
                min="0"
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="0"
              />
            </div>

            {/* Weather Temperature */}
            <div className="mb-1">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Weather Temperature
              </label>
              <select
                name="weatherTemp"
                value={formData.weatherTemp}
                onChange={handleChange}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                {weatherTempOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Weather Type */}
            <div className="mb-2">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Weather Type
              </label>
              <select
                name="weatherType"
                value={formData.weatherType}
                onChange={handleChange}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                {weatherTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Daily Notes/Reflection */}
            <div className="mb-2">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Daily Notes / Reflection
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="4"
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline resize-vertical"
                placeholder="How was your day? Any memorable moments, trail conditions, people you met, or thoughts about the journey..."
              />
              <p className="text-xs text-gray-600 mt-1">
                Optional: Share your thoughts, experiences, or observations from today's hike
              </p>
            </div>

            {/* Submit Button */}
            <div className="space-y-3">
              <button
                type="submit"
                disabled={loading}
                className={`w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Saving...' : existingEntryId || existingTempId ? 'Update Entry' : (isOnline ? 'Add Entry' : 'Save Offline')}
              </button>
              
              {(existingEntryId || existingTempId) && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className={`w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
                    loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  🗑️ Delete Entry
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default UpdateHike;